import React from 'react'
import { useFormsStyles } from '../forms.css'
import { Form } from 'react-final-form'
import { SubmitButtonProps } from 'components/common/submitButton'
import { Trans, useTranslation } from 'react-i18next'
import { BusinessHour, BusinessHours, DayOfWeek } from '@sugg-gestion/ubidreams-react-suggpro'
import BusinessHoursField from '../components/businessHoursField'

/**
 * Props for the HoursForm component
 */
interface Props {
    /** Form field subscription configuration for performance optimization */
    subscription?: { [property: string]: boolean }
    /** Callback function triggered on form submission */
    onSubmit: (values: HoursFormState) => void
    /** Render prop function that receives form content and action buttons */
    children: (content: React.ReactNode, actions: Array<SubmitButtonProps>) => React.ReactNode
    /** Optional CSS class name for the form element */
    formClassName?: string
    /** Flag indicating if submission is in progress */
    inProgress: boolean
    /** Initial values for the form fields */
    initialValues: HoursFormState
}

/**
 * State shape for the business hours form
 */
export interface HoursFormState {
    /** Regular business hours organized by day of week */
    regularHours?: BusinessHours
}

/**
 * Error structure for a single business hour slot
 */
type RegularHoursError = {
    /** Error message for the time slot */
    message?: string
    /** Array of error messages for associated services */
    services: Array<string>
}

/**
 * Error structure for regular hours, organized by day of week
 */
type RegularHoursErrors = {
    [day in DayOfWeek]: Array<RegularHoursError>
}

/**
 * Complete error structure for the hours form
 */
interface HoursFormError {
    /** Validation errors for regular business hours */
    regularHours?: RegularHoursErrors
}
/**
 * Form component for managing establishment business hours
 * Handles regular hours for each day of the week with validation for time slots and services
 */
const HoursForm: React.FC<Props> = ({
    subscription = {
        submitting: true,
        pristine: true,
        values: true,
    },
    onSubmit,
    children,
    formClassName,
    inProgress,
    initialValues,
}) => {
    const formsClasses = useFormsStyles()
    const { t } = useTranslation()

    /**
     * Converts a time string in HH:MM format to total minutes
     * @param hhmm - Time string in format "HH:MM"
     * @returns Total minutes from midnight
     */
    const toMinutes = (hhmm: string): number => {
        const [h, m] = hhmm.split(':').map((x) => Number(x))
        return h * 60 + m
    }

    /**
     * Validates service hours within a business hour slot and returns an array of error messages
     * @param value - The business hour containing services to validate
     * @returns Array of error messages, indexed by service position
     */
    const appendServicesError = (value: BusinessHour): Array<string> => {
        const errors: Array<string> = []
        if (value.services && value.services.length > 0) {
            const parentStart = toMinutes(value.startTime)
            let parentEnd = toMinutes(value.endTime)
            const parentCrossesMidnight = parentEnd < parentStart
            if (parentCrossesMidnight) parentEnd += 24 * 60

            /**
             * Normalizes service start and end times to handle midnight crossing
             * Adjusts times to a continuous timeline when parent hours cross midnight
             */
            const normalizeServiceMinutes = (serviceStart: string, serviceEnd: string) => {
                let sStart = toMinutes(serviceStart)
                let sEnd = toMinutes(serviceEnd)

                // If parent hours cross midnight, times "after midnight" must be projected to +24h
                if (parentCrossesMidnight) {
                    if (sStart < parentStart) sStart += 24 * 60
                    if (sEnd < parentStart) sEnd += 24 * 60
                }

                // Handle service hours that cross midnight (rare, but possible)
                if (sEnd < sStart) sEnd += 24 * 60

                return { sStart, sEnd }
            }

            for (let i = 0; i < value.services.length; i++) {
                const service = value.services[i]
                const { sStart, sEnd } = normalizeServiceMinutes(service.startTime, service.endTime)

                // Service must start after the business opens (on the timeline)
                if (sStart < parentStart) {
                    errors[i] = t('form-validation.serviceHourStart')
                    continue
                }

                // Service must end before the business closes (on the timeline)
                if (sEnd > parentEnd) {
                    errors[i] = t('form-validation.serviceHourEnd')
                    continue
                }

                // Check for overlap: compare with the previous service on the same timeline
                if (i > 0) {
                    const prev = value.services[i - 1]
                    const prevNorm = normalizeServiceMinutes(prev.startTime, prev.endTime)
                    if (sStart < prevNorm.sEnd) {
                        errors[i] = t('form-validation.hoursOverlap')
                        continue
                    }
                }

                // Rule "max 6am" (preserve current intention, but in minutes)
                // Prevents a service "entirely after 06:00" when it crosses midnight
                const limit = 6 * 60 // 06:00
                const sStartRaw = toMinutes(service.startTime)
                const sEndRaw = toMinutes(service.endTime)
                if (
                    i === value.services.length - 1 &&
                    sEndRaw < sStartRaw &&
                    sEndRaw > limit - 1 &&
                    sStartRaw > limit - 1
                ) {
                    errors[i] = t('form-validation.hoursMax')
                }
            }
        }
        return errors
    }

    /**
     * Validates regular business hours for a specific day and appends errors to the errors object
     * @param errors - The errors object to populate with validation errors
     * @param day - The day of week being validated
     * @param value - The business hours data for all days
     */
    const appendRegularHoursError = (errors: HoursFormError, day: DayOfWeek, value: BusinessHours) => {
        if (value[day]) {
            const dayHours = value[day]
            // Initialize the regularHours error structure if it doesn't exist
            if (errors.regularHours === undefined) {
                errors.regularHours = {
                    [DayOfWeek.Monday]: [],
                    [DayOfWeek.Tuesday]: [],
                    [DayOfWeek.Wednesday]: [],
                    [DayOfWeek.Thursday]: [],
                    [DayOfWeek.Friday]: [],
                    [DayOfWeek.Saturday]: [],
                    [DayOfWeek.Sunday]: [],
                }
            }
            for (let i = 0; i < dayHours.length; i++) {
                const regularHour = dayHours[i]
                let previousHour: BusinessHour | undefined = undefined
                if (i > 0) {
                    previousHour = dayHours[i - 1]
                }
                // Maximum of 2 time slots allowed per day
                if (i > 1) {
                    errors.regularHours[day][i] = {
                        message: t('form-validation.hoursMaxSlot'),
                        services: appendServicesError(regularHour),
                    }
                    continue
                }
                // Start time cannot equal end time
                if (regularHour.startTime === regularHour.endTime) {
                    errors.regularHours[day][i] = {
                        message: t('form-validation.hours'),
                        services: appendServicesError(regularHour),
                    }
                    continue
                }
                // Check for overlap with the previous time slot
                if (previousHour && regularHour.startTime < previousHour.endTime) {
                    errors.regularHours[day][i] = {
                        message: t('form-validation.hoursOverlap'),
                        services: appendServicesError(regularHour),
                    }
                    continue
                }
                // Last slot cannot cross midnight and end after 06:00 if it also starts after 06:00
                if (
                    i === dayHours.length - 1 &&
                    regularHour.endTime < regularHour.startTime &&
                    regularHour.endTime > '05:59' &&
                    regularHour.startTime > '05:59'
                ) {
                    errors.regularHours[day][i] = {
                        message: t('form-validation.hoursMax'),
                        services: appendServicesError(regularHour),
                    }
                    continue
                }
                // Validate nested service hours
                const servicesErrors = appendServicesError(regularHour)
                if (servicesErrors.length > 0) {
                    errors.regularHours[day][i] = {
                        services: servicesErrors,
                    }
                }
            }
        }
    }

    /**
     * Validates the entire form state and returns validation errors
     * @param values - The form values to validate
     * @returns Object containing all validation errors
     */
    const validate = (values: HoursFormState) => {
        const errors: HoursFormError = {}

        // Validate regular hours for each day of the week
        if (values.regularHours) {
            appendRegularHoursError(errors, DayOfWeek.Monday, values.regularHours)
            appendRegularHoursError(errors, DayOfWeek.Tuesday, values.regularHours)
            appendRegularHoursError(errors, DayOfWeek.Wednesday, values.regularHours)
            appendRegularHoursError(errors, DayOfWeek.Thursday, values.regularHours)
            appendRegularHoursError(errors, DayOfWeek.Friday, values.regularHours)
            appendRegularHoursError(errors, DayOfWeek.Saturday, values.regularHours)
            appendRegularHoursError(errors, DayOfWeek.Sunday, values.regularHours)
        }

        return errors
    }

    /**
     * Converts numeric object keys to string keys prefixed with "key"
     * Required because react-final-form doesn't properly handle numeric keys in objects
     * See: https://github.com/final-form/react-final-form/blob/master/docs/faq.md#why-cant-i-have-numeric-keys-in-an-object
     * @param values - Object with numeric keys
     * @returns Object with stringified keys
     */
    const stringifyKeys = (values: any) =>
        Object.keys(values).reduce((result, key) => {
            // @ts-ignore
            result[`key${key}`] = values[key]
            return result
        }, {})

    /**
     * Converts string keys prefixed with "key" back to numeric keys
     * Reverses the stringifyKeys transformation for form submission
     * @param values - Object with stringified keys
     * @returns Object with numeric keys
     */
    const destringifyKeys = (values: any) =>
        Object.keys(values).reduce((result, key) => {
            // @ts-ignore
            result[Number(key.substring(3))] = values[key]
            return result
        }, {})
    return (
        <Form
            initialValues={{
                regularHours: stringifyKeys(
                    initialValues.regularHours ?? {
                        [DayOfWeek.Monday]: [],
                        [DayOfWeek.Tuesday]: [],
                        [DayOfWeek.Wednesday]: [],
                        [DayOfWeek.Thursday]: [],
                        [DayOfWeek.Friday]: [],
                        [DayOfWeek.Saturday]: [],
                        [DayOfWeek.Sunday]: [],
                    },
                ),
            }}
            subscription={subscription}
            // debug={console.log}
            onSubmit={(values) =>
                onSubmit({
                    regularHours: destringifyKeys(values.regularHours) as BusinessHours,
                })
            }
            validate={(values) => {
                const errors = validate({
                    regularHours: destringifyKeys(values.regularHours) as BusinessHours,
                })
                return {
                    regularHours: stringifyKeys(errors.regularHours),
                }
            }}
            keepDirtyOnReinitialize
        >
            {({ handleSubmit, submitting, values }) => (
                <form onSubmit={handleSubmit} noValidate className={formClassName}>
                    {children(
                        <>
                            <BusinessHoursField
                                name={'regularHours[key' + DayOfWeek.Monday + ']'}
                                day={DayOfWeek.Monday}
                                values={values}
                            />
                            <BusinessHoursField
                                name={'regularHours[key' + DayOfWeek.Tuesday + ']'}
                                day={DayOfWeek.Tuesday}
                                values={values}
                            />
                            <BusinessHoursField
                                name={'regularHours[key' + DayOfWeek.Wednesday + ']'}
                                day={DayOfWeek.Wednesday}
                                values={values}
                            />
                            <BusinessHoursField
                                name={'regularHours[key' + DayOfWeek.Thursday + ']'}
                                day={DayOfWeek.Thursday}
                                values={values}
                            />
                            <BusinessHoursField
                                name={'regularHours[key' + DayOfWeek.Friday + ']'}
                                day={DayOfWeek.Friday}
                                values={values}
                            />
                            <BusinessHoursField
                                name={'regularHours[key' + DayOfWeek.Saturday + ']'}
                                day={DayOfWeek.Saturday}
                                values={values}
                            />
                            <BusinessHoursField
                                name={'regularHours[key' + DayOfWeek.Sunday + ']'}
                                day={DayOfWeek.Sunday}
                                values={values}
                            />
                            {/*<RegularHoursField*/}
                            {/*    name="regularHours"*/}
                            {/*    serviceTime={(day: DayOfWeek, startTime: string, endTime: string) => (*/}
                            {/*        <Grid item xs={12}>*/}
                            {/*            <ServiceTimesField*/}
                            {/*                name={'serviceTimes[' + day + ']'}*/}
                            {/*                startTime={startTime}*/}
                            {/*                endTime={endTime}*/}
                            {/*            />*/}
                            {/*        </Grid>*/}
                            {/*    )}*/}
                            {/*/>*/}
                        </>,
                        [
                            {
                                type: 'submit',
                                loading: submitting || inProgress,
                                className: formsClasses.submit,
                                children: <Trans i18nKey="actions.next" />,
                            },
                        ],
                    )}
                </form>
            )}
        </Form>
    )
}
export default HoursForm
